import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography, CircularProgress, TextField, Stack } from '@mui/material';
import { useTheme } from '@emotion/react';
import { digilockerService } from '../../services/digilockerService';
import { totalCreditsAndRemaining } from "../../services/creditsService";
import { useDispatch } from "react-redux";
import { remainingCredits } from "../../store/reducers/auth";

export function DigilockerAuth({ onAuthSuccess, onAuthError, disabled = false, setCurlMethod, setCurlUrl, setCurlBody, apiUrl }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');  
  const [isNumericMode, setIsNumericMode] = useState(true);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'digilocker_state') {
        const value = event.newValue;
        setInputValue(value);
        const newIsNumericMode = /^\d+$/.test(value);
        setIsNumericMode(newIsNumericMode);
        
        const curlSetters = setCurlMethod && setCurlUrl && setCurlBody;
        if (curlSetters && !newIsNumericMode && value) {
          setCurlMethod('GET');
          setCurlUrl(`/v1/digilocker-verification?state=${encodeURIComponent(value)}`);
          setCurlBody(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [setCurlMethod, setCurlUrl, setCurlBody]);

  useEffect(() => {
    const curlSetters = setCurlMethod && setCurlUrl && setCurlBody;
    
    if (curlSetters) {
      localStorage.removeItem('digilocker_state');
      
      setCurlMethod('POST');
      setCurlUrl('/v1/digilocker-auth-url');
      setCurlBody(null);
    }
    
    return () => {
      localStorage.removeItem('digilocker_state');
    };
  }, [setCurlMethod, setCurlUrl, setCurlBody]);

  const handleKYCFlow = async () => {
    
    try {
      setLoading(true);
      const data = await digilockerService.getAuthUrl(inputValue);
      sessionStorage.setItem('restore_category', sessionStorage.getItem('current_category') || 'DigiLocker');
      sessionStorage.setItem('restore_api', sessionStorage.getItem('current_api') || '');
      window.open(data.authUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      onAuthError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectFetch = async () => {
    try {
      setFetchLoading(true);
      const userData = await digilockerService.getUserData(inputValue, "state");
      onAuthSuccess?.(userData);
      setTimeout(async () => {
        const remainingCredit = await totalCreditsAndRemaining();
        if (remainingCredit?.status === 200) {
          dispatch(
            remainingCredits({
              credits: remainingCredit?.data?.credit?.remainingCredit,
            })
          );
        }
      }, 2000);
      setInputValue('');
      localStorage.removeItem('digilocker_state');
    } catch (error) {
      let errorData;
      if (error.response?.data) {
        errorData = error.response.data;
      } else if (error.message) {
        errorData = { error: error.message };
      } else {
        errorData = { error: 'Failed to fetch user data' };
      }
      
      const errorWithStatus = {
        ...error,
        response: {
          ...error.response,
          data: errorData,
          status: error.response?.status || 500
        }
      };
      onAuthError?.(errorWithStatus);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value.trim();
    setInputValue(value);
    const newIsNumericMode = /^\d+$/.test(value);
    setIsNumericMode(newIsNumericMode);
    onAuthSuccess?.(null);
    
    const curlSetters = setCurlMethod && setCurlUrl && setCurlBody;
    
    if (curlSetters) {
      if (newIsNumericMode && value.length === 12) {
        // First API: Auth URL with Aadhaar number
        setCurlMethod('POST');
        setCurlUrl('/v1/digilocker-auth-url');
        setCurlBody({ aadhaarNumber: value });
      } else if (!newIsNumericMode && value) {
        // Second API: Verification with state parameter
        setCurlMethod('GET');
        setCurlUrl(`/v1/digilocker-verification?state=${encodeURIComponent(value)}`);
        setCurlBody(null);
      } else {
        // Default fallback - empty input should disable curl button
        setCurlMethod('POST');
        setCurlUrl('/v1/digilocker-auth-url');
        setCurlBody(null);
      }
    }
  };

  return (
    <Stack spacing={5} sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography sx={{ color: theme.palette.grey.darken4 }} variant="body1">
            {isNumericMode ? "Aadhaar Number" : "Transaction Id"}
          </Typography>
          <TextField
            sx={{ width: '75%' }}
            value={inputValue}
            onChange={handleChange}
            placeholder={isNumericMode ? "Enter Aadhaar Number" : "Enter Transaction Id"}
            variant="outlined"
            inputProps={{
              maxLength: isNumericMode ? 12 : undefined,
              inputMode: isNumericMode ? 'numeric' : 'text',
              pattern: isNumericMode ? '\\d*' : undefined
            }}
          />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2}>
        {/* Aadhaar Flow */}
        <Button
          variant="contained"
          onClick={handleKYCFlow}
          disabled={disabled || loading || fetchLoading || !(/^\d{12}$/.test(inputValue))}
          sx={{ width: 'fit-content' }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Connecting...
            </>
          ) : (
            'Proceed with KYC'
          )}
        </Button>

        {/* Consent Flow */}
        <Button
          variant="outlined"
          onClick={handleDirectFetch}
          disabled={disabled || loading || fetchLoading || isNumericMode || !inputValue}
          sx={{ width: 'fit-content' }}
        >
          {fetchLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Fetching...
            </>
          ) : (
            'Consent'
          )}
        </Button>
      </Stack>
    </Stack>
  );
}

DigilockerAuth.propTypes = {
  onAuthSuccess: PropTypes.func,
  onAuthError: PropTypes.func,
  disabled: PropTypes.bool,
  setCurlMethod: PropTypes.func,
  setCurlUrl: PropTypes.func,
  setCurlBody: PropTypes.func,
  apiUrl: PropTypes.string,
};